import React, { useLayoutEffect, useRef } from 'react';
import {
  combinedBoundsFromRects,
  normalizeGhostDropSlots,
  positionClone,
  stripInteractiveChrome,
} from './canvasDragMirrorDom';
import { resolveBlockDragMirrorElement } from './dnd/block-drag-mirror';

export type CanvasDragMirrorItem = {
  frameSelector: string;
  contentSelector?: string;
  tightBlockContent?: boolean;
  useFrameBounds?: boolean;
  preserveDropSlots?: boolean;
};

type CanvasDragMirrorProps = {
  items: CanvasDragMirrorItem[];
};

export function CanvasDragMirror({ items }: CanvasDragMirrorProps) {
  const hostRef = useRef<HTMLDivElement>(null);
  const itemsKey = items
    .map(item => `${item.frameSelector}:${item.contentSelector ?? ''}`)
    .join('\u0001');

  useLayoutEffect(() => {
    const host = hostRef.current;
    if (!host || items.length === 0) {
      return;
    }

    const entries = items
      .map(item => {
        const frame = document.querySelector(item.frameSelector);
        if (!(frame instanceof HTMLElement)) {
          return null;
        }

        const contentSource = item.contentSelector
          ? frame.querySelector(item.contentSelector)
          : frame;
        if (!(contentSource instanceof HTMLElement)) {
          return null;
        }

        const useTightBlockMirror =
          item.tightBlockContent === true &&
          frame.getAttribute('data-level') === 'block';
        const mirrorContent = useTightBlockMirror
          ? resolveBlockDragMirrorElement(contentSource)
          : contentSource;
        const frameRect = frame.getBoundingClientRect();
        const mirrorRect = mirrorContent.getBoundingClientRect();
        const contentRect = contentSource.getBoundingClientRect();
        const boundsRect = useTightBlockMirror
          ? mirrorRect
          : item.useFrameBounds === true
            ? frameRect
            : contentRect;
        const positionRect =
          useTightBlockMirror
            ? mirrorRect
            : item.useFrameBounds === true
              ? contentRect
              : boundsRect;

        return {
          mirrorContent,
          boundsRect,
          positionRect,
          preserveDropSlots: item.preserveDropSlots === true,
          useTightBlockMirror,
        };
      })
      .filter((entry): entry is NonNullable<typeof entry> => entry !== null);

    if (entries.length === 0) {
      return;
    }

    const combinedBounds = combinedBoundsFromRects(
      entries.map(entry => entry.boundsRect),
    );
    if (!combinedBounds) {
      return;
    }

    const ghostRoot = document.createElement('div');
    ghostRoot.className =
      entries.length > 1
        ? 'email-builder-canvas-drag-mirror email-builder-canvas-drag-mirror--multi'
        : 'email-builder-canvas-drag-mirror';
    ghostRoot.style.position = 'relative';
    ghostRoot.style.opacity = '1';
    ghostRoot.style.overflow = 'visible';
    ghostRoot.style.background = 'transparent';
    ghostRoot.style.width = `${combinedBounds.width}px`;
    ghostRoot.style.height = `${combinedBounds.height}px`;

    for (const entry of entries) {
      const contentClone = entry.mirrorContent.cloneNode(true);
      if (!(contentClone instanceof HTMLElement)) {
        continue;
      }

      positionClone(contentClone, entry.positionRect, combinedBounds);
      if (entry.useTightBlockMirror) {
        contentClone.classList.add('email-builder-drag-ghost-content');
      }
      stripInteractiveChrome(contentClone, entry.preserveDropSlots);
      if (entry.preserveDropSlots) {
        normalizeGhostDropSlots(contentClone);
      }
      ghostRoot.appendChild(contentClone);
    }

    host.style.width = `${combinedBounds.width}px`;
    host.style.minHeight = `${combinedBounds.height}px`;
    host.replaceChildren(ghostRoot);
  }, [itemsKey]);

  return <div ref={hostRef} />;
}
